/**
 * D9: Oefenlab UI State
 * Source: src/learningLab/LearningLabPage.tsx
 */

import { isBackendEnabled, STORAGE_KEY, readJson, writeJson, removeKey } from '../helpers';

// Per domein — dit domein heeft nog geen backend. Zie helpers.ts.
const USE_BACKEND = isBackendEnabled('oefenlab');
const FLUENCY_INTRO_KEY = 'fluency_intro_seen';
const SPRINT_INTRO_KEY = 'sprint_intro_seen';
const FREE_PRACTICE_INTRO_KEY = 'oefenlab_free_practice_intro_seen';
const CHALLENGE_INTRO_KEY = 'oefenlab_challenge_intro_seen';
const LAST_OEFENLAB_SESSION_KEY = 'oefenlab_last_session';
const NEWLY_UNLOCKED_CHALLENGE_KEY = 'oefenlab_newly_unlocked_challenge';
const PERSONAL_RECORDS_KEY = 'oefenlab_personal_records';

/**
 * Leerling-scoping voor de drie sleutels die PER KIND verschillen.
 *
 * Deze drie stonden onder een vaste sleutel, zonder leerling-id. Op een gedeeld
 * apparaat — een gezinstablet of een klaslokaalcomputer, precies waar dit
 * product voor bedoeld is — betekende dat: kind B las de persoonlijke records
 * van kind A, zag "Verder waar je was" met de laatste sessie van A, en kreeg de
 * "Uitdaging ontgrendeld"-viering van A te zien. Het overschreef ze ook, dus A's
 * records verdwenen zodra B beter presteerde.
 *
 * De vier intro-vlaggen (`*_intro_seen`) en de klascode-banner blijven bewust
 * ONgescoped: die zeggen "deze uitleg is op dit apparaat al eens gezien". Dat is
 * een eigenschap van het apparaat, geen prestatie van een kind — en een tweede
 * kind twee keer dezelfde uitleg voorschotelen is geen fout.
 *
 * Zonder leerling-id valt de sleutel terug op de oude, ongescope naam. Dat is
 * geen nette fallback maar wel de eerlijke: liever de historische sleutel dan
 * `..._undefined`, waar de records van elk kind zonder id op elkaar stapelen.
 */
function scoped(baseKey: string, learnerId: string): string {
  return learnerId ? `${baseKey}_${learnerId}` : baseKey;
}

/** HC-1 fix: localStorage key for klascode banner — now uses STORAGE_KEY prefix */
const KLASCODE_BANNER_KEY = `${STORAGE_KEY}_klascode_banner_dismissed`;
export interface SprintRecord {
  bestScore: number;
  fastestTimeMs: number;
}

/**
 * ⚠️ `sprint` is een MAP per familyId, geen enkel record.
 *
 * Dit type verklaarde het als één vlak object (`{ bestScore, totalSessions }`),
 * terwijl `SessionSummary` er al sinds de invoering per onderwerp in schrijft
 * (`sprint[familyId] = { bestScore, fastestTimeMs }`). Drie vormen die niet op
 * elkaar pasten: het type, de schrijver, en de lezer in `useLabPersonalization`
 * die `sprint.bestScore` uitlas — op de geschreven vorm altijd `undefined`, dus
 * het sprintrecord verscheen NOOIT op de kaart. Geen crash, geen melding: de
 * regel bleef gewoon weg.
 *
 * Per onderwerp is ook de juiste vorm. Een sprint gaat over één familie; één
 * globale "beste sprintscore" vergelijkt breuken met werkwoordspelling en zegt
 * daarmee niets.
 */
export interface PersonalRecords {
  fluency?: {
    fastestAvgMs: number;
    longestStreak: number;
    bestScore: number;
  };
  sprint?: Record<string, SprintRecord>;
  challenge?: {
    highestTier: number;
    bestScore: number;
    totalCompleted: number;
  };
}
export async function getFluencyIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    return localStorage.getItem(FLUENCY_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}
export async function setFluencyIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    if (seen) {
      localStorage.setItem(FLUENCY_INTRO_KEY, '1');
    } else {
      localStorage.removeItem(FLUENCY_INTRO_KEY);
    }
  } catch {

    // silent
  }
}
export async function getSprintIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    return localStorage.getItem(SPRINT_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}
export async function setSprintIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    if (seen) {
      localStorage.setItem(SPRINT_INTRO_KEY, '1');
    } else {
      localStorage.removeItem(SPRINT_INTRO_KEY);
    }
  } catch {

    // silent
  }
}
export async function getFreePracticeIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    return localStorage.getItem(FREE_PRACTICE_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}
export async function setFreePracticeIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  writeJson(FREE_PRACTICE_INTRO_KEY, seen);
}
export async function getChallengeIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    return localStorage.getItem(CHALLENGE_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}
export async function setChallengeIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {

    // TODO: backend call
  } else {
    if (seen) localStorage.setItem(CHALLENGE_INTRO_KEY, '1');else localStorage.removeItem(CHALLENGE_INTRO_KEY);
  }
}

/** Get klascode banner dismissed state */
export async function getKlascodeBannerDismissed(): Promise<boolean> {
  if (USE_BACKEND) {
    // TODO: backend call
    return false;
  }
  // Migration: read old key if new key not found
  const newVal = localStorage.getItem(KLASCODE_BANNER_KEY);
  if (newVal !== null) return newVal === 'true';
  const oldVal = localStorage.getItem('klascode_banner_dismissed');
  if (oldVal === 'true') {
    // Migrate to new key
    localStorage.setItem(KLASCODE_BANNER_KEY, 'true');
    localStorage.removeItem('klascode_banner_dismissed');
    return true;
  }
  return false;
}

/** Set klascode banner dismissed state */
export async function setKlascodeBannerDismissed(dismissed: boolean): Promise<void> {
  if (USE_BACKEND) {

    // TODO: backend call
  } else {
    if (dismissed) localStorage.setItem(KLASCODE_BANNER_KEY, 'true');else localStorage.removeItem(KLASCODE_BANNER_KEY);
    // Clean up old key if present
    localStorage.removeItem('klascode_banner_dismissed');
  }
}

/** Get the last active session state for Oefenlab (per leerling) */
export async function getLastOefenlabSession(learnerId: string): Promise<{
  modeId: string;
  familyId?: string;
  timestamp: number;
  score?: number;
} | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson(scoped(LAST_OEFENLAB_SESSION_KEY, learnerId), null);
}

/** Save the last oefenlab session details (per leerling) */
export async function saveLastOefenlabSession(learnerId: string, info: {
  modeId: string;
  familyId?: string;
  timestamp: number;
  score?: number;
}): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return writeJson(scoped(LAST_OEFENLAB_SESSION_KEY, learnerId), info);
}

/** Get newly unlocked challenge familyId (per leerling) */
export async function getNewlyUnlockedChallenge(learnerId: string): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson(scoped(NEWLY_UNLOCKED_CHALLENGE_KEY, learnerId), null);
}

/** Set newly unlocked challenge familyId (per leerling) */
export async function setNewlyUnlockedChallenge(learnerId: string, familyId: string): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return writeJson(scoped(NEWLY_UNLOCKED_CHALLENGE_KEY, learnerId), familyId);
}

/** Clear newly unlocked challenge (per leerling) */
export async function clearNewlyUnlockedChallenge(learnerId: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  removeKey(scoped(NEWLY_UNLOCKED_CHALLENGE_KEY, learnerId));
}

/** Get personal records for oefenlab modes (per leerling) */
export async function getPersonalRecords(learnerId: string): Promise<PersonalRecords> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson<PersonalRecords>(scoped(PERSONAL_RECORDS_KEY, learnerId), {});
}

/** Save personal records for oefenlab modes (per leerling) */
export async function savePersonalRecords(learnerId: string, records: PersonalRecords): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  // Levert `false` op wanneer de opslag vol is of geweigerd wordt. De aanroeper
  // MOET daarop reageren: een record vieren dat niet is weggeschreven, is het
  // kind een prestatie toekennen die bij de volgende sessie weer weg is.
  return writeJson(scoped(PERSONAL_RECORDS_KEY, learnerId), records);
}